import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
  type ChangeEvent,
  type Dispatch,
  type SetStateAction,
} from "react";

import { Button, Checkbox } from "@king-ict/eupisi-ui/components";
import { PagedResult } from "@king-ict/eupisi-ui/models";
import { nameof } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { FormCard, InstitutionSelect } from "../../../../components";
import { ConfirmModal } from "../../../../components/FormControls/ConfirmModal";
import type {
  ValidationFailure,
  Selectable,
  SchoolYear,
} from "../../../../models";
import type {
  SaveSchoolCandidateInstitutionAssignment,
  SchoolCandidateResidenceChange,
} from "../../../../models/SchoolCandidate";
import type { InstitutionCodeBook } from "../../../../models/Institution/InstitutionCodeBook";
import type { SaveResult } from "../../../../models/Shared/SaveResult";
import type {
  SchoolCandidateResidencesDownloadRequest,
  SchoolCandidateResidencesDownloadResult,
} from "../../../../queries";

import {
  createNextSelection,
  hasAnySelectedWithInstitution,
  hasSelectedCandidates,
  hasSelectedCandidatesInstitutionAndRequest,
} from "./utils";
import { buildTableDataWithSelection } from "../util";
import { DEFAULT_UPDATE_INSTITUTION_ASSIGNMENT } from "../constants";
import { MissingAddressWarningModal } from "./MissingAddressWarningModal";
import { useDownloadAddresses } from "./useDownloadAddresses";

type AssignmentRequest = (
  payload: SaveSchoolCandidateInstitutionAssignment
) => {
  unwrap: () => Promise<SaveResult<SaveSchoolCandidateInstitutionAssignment>>;
};

type DownloadAddressesRequest = (
  args: SchoolCandidateResidencesDownloadRequest
) => {
  unwrap: () => Promise<SaveResult<SchoolCandidateResidencesDownloadResult>>;
};

type SchoolCandidateAssignCardProps = {
  countyId?: number;
  batchId: number | null;
  institutions?: InstitutionCodeBook[];
  isSchoolYearEditable: boolean;
  currentSchoolYear: SchoolYear;
  selectedSchoolYearId: number;
  onSearch: () => void;
  saveInstitutionAssignmentRequest: AssignmentRequest;
  setErrors: Dispatch<SetStateAction<ValidationFailure[] | undefined>>;
  setInstitutionAssignmentRequest: AssignmentRequest;
  setTableData: Dispatch<
    SetStateAction<PagedResult<SchoolCandidateResidenceChange & Selectable>>
  >;
  tableData: PagedResult<SchoolCandidateResidenceChange & Selectable>;
  downloadAddressesRequest: DownloadAddressesRequest;
};

export type SchoolCandidateAssignCardHandle = {
  handleCountyChange: (countyId?: number) => void;
  handleSchoolCandidateCheckedChange: (
    event: ChangeEvent<HTMLInputElement>
  ) => void;
  resetAssignmentState: () => void;
};

export const SchoolCandidateAssignCard = forwardRef<
  SchoolCandidateAssignCardHandle,
  SchoolCandidateAssignCardProps
>(
  (
    {
      countyId,
      batchId,
      institutions,
      isSchoolYearEditable,
      currentSchoolYear,
      selectedSchoolYearId,
      onSearch,
      saveInstitutionAssignmentRequest,
      setErrors,
      setInstitutionAssignmentRequest,
      setTableData,
      tableData,
      downloadAddressesRequest,
    },
    ref
  ) => {
    const { t } = useTranslation();

    const [confirmUnassign, setConfirmUnassign] = useState<
      "confirm" | "message" | "error" | undefined
    >();
    const [isAddressChangeButtonClicked, setIsAddressChangeButtonClicked] =
      useState(false);
    const [addAssignmentEnabled, setAddAssignmentEnabled] = useState(false);
    const [clearAssignmentEnabled, setClearAssignmentEnabled] = useState(false);
    const [
      addHouseNumberInstitutionEnabled,
      setAddHouseNumberInstitutionEnabled,
    ] = useState(false);
    const [isDownloadAddressesEnabled, setDownloadAddressesEnabled] =
      useState(false);
    const [selectAll, setSelectAll] = useState(false);
    const [updateInstitutionAssignment, setUpdateInstitutionAssignment] =
      useState<SaveSchoolCandidateInstitutionAssignment>(
        DEFAULT_UPDATE_INSTITUTION_ASSIGNMENT
      );

    const setAssignmentButtons = useCallback(
      (update: SaveSchoolCandidateInstitutionAssignment) => {
        const anySelected = hasSelectedCandidates(update);
        const anyWithInstitution = hasAnySelectedWithInstitution(
          update,
          tableData
        );

        setAddAssignmentEnabled(
          anySelected &&
            !anyWithInstitution &&
            update.institutionId !== undefined
        );

        setClearAssignmentEnabled(anySelected && anyWithInstitution);

        setDownloadAddressesEnabled(
          !!hasSelectedCandidatesInstitutionAndRequest(update, tableData)
        );

        setAddHouseNumberInstitutionEnabled(
          anySelected &&
            !anyWithInstitution &&
            update.institutionId === undefined
        );
      },
      [tableData]
    );

    const clearAssignmentButtons = useCallback(() => {
      setSelectAll(false);
      setClearAssignmentEnabled(false);
      setAddAssignmentEnabled(false);
      setAddHouseNumberInstitutionEnabled(false);
    }, []);

    const resetAssignmentState = useCallback(() => {
      setSelectAll(false);
      setUpdateInstitutionAssignment(DEFAULT_UPDATE_INSTITUTION_ASSIGNMENT);

      clearAssignmentButtons();
    }, [clearAssignmentButtons]);

    const handleAssignmentInstitutionChange = useCallback(
      (institutionId?: number) => {
        const nextUpdate: SaveSchoolCandidateInstitutionAssignment = {
          ...updateInstitutionAssignment,
          institutionId: institutionId,
        };

        setUpdateInstitutionAssignment(nextUpdate);
        setAssignmentButtons(nextUpdate);
      },
      [setAssignmentButtons, updateInstitutionAssignment]
    );

    const handleSchoolCandidateCheckedChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const schoolCandidateId = Number.parseInt(event.target.value, 10);

        if (Number.isNaN(schoolCandidateId)) {
          return;
        }

        const isChecked = event.target.checked;

        const nextSchoolCandidateList = createNextSelection(
          updateInstitutionAssignment.schoolCandidateList,
          schoolCandidateId,
          isChecked
        );

        const nextUpdate: SaveSchoolCandidateInstitutionAssignment = {
          ...updateInstitutionAssignment,
          schoolCandidateList: nextSchoolCandidateList,
        };
        const nextTableData = buildTableDataWithSelection(
          tableData,
          nextSchoolCandidateList
        );

        const areAllSelected = nextTableData.results.every(
          (candidate: SchoolCandidateResidenceChange & Selectable) =>
            candidate.isSelected
        );

        setSelectAll(areAllSelected);

        setTableData(nextTableData);
        setUpdateInstitutionAssignment(nextUpdate);
        setAssignmentButtons(nextUpdate);
      },
      [
        setAssignmentButtons,
        setTableData,
        tableData,
        updateInstitutionAssignment,
      ]
    );

    const handleSelectAllCheckedChange = useCallback(() => {
      const isChecked = !selectAll;

      setSelectAll(isChecked);

      const nextSelectedIds = isChecked
        ? tableData.results.map(
            (candidate: SchoolCandidateResidenceChange & Selectable) =>
              candidate.schoolCandidateId
          )
        : [];

      const nextTableData = buildTableDataWithSelection(
        tableData,
        nextSelectedIds
      );

      const nextUpdate: SaveSchoolCandidateInstitutionAssignment = {
        ...updateInstitutionAssignment,
        schoolCandidateList: nextSelectedIds,
      };

      setTableData(nextTableData);
      setUpdateInstitutionAssignment(nextUpdate);
      setAssignmentButtons(nextUpdate);
    }, [
      selectAll,
      setAssignmentButtons,
      setTableData,
      tableData,
      updateInstitutionAssignment,
    ]);

    const handleCountyChange = useCallback(
      (countyId?: number) => {
        if (
          !institutions?.length ||
          !updateInstitutionAssignment.institutionId
        ) {
          return;
        }

        const countyInstitutions = institutions.filter(
          (institution) => institution.countyId === countyId
        );

        if (
          countyInstitutions.find(
            (institution) =>
              institution.id === updateInstitutionAssignment.institutionId
          ) === undefined
        ) {
          handleAssignmentInstitutionChange(undefined);
        }
      },
      [
        handleAssignmentInstitutionChange,
        institutions,
        updateInstitutionAssignment.institutionId,
      ]
    );

    const handleClearAssignmentsClicked = useCallback(async () => {
      const save: SaveSchoolCandidateInstitutionAssignment = {
        ...updateInstitutionAssignment,
        institutionId: undefined,
      };

      try {
        const result = await saveInstitutionAssignmentRequest(save).unwrap();

        setErrors(result.validationFailures);

        if (result.isValid) {
          setConfirmUnassign("message");
        }

        onSearch();
        setConfirmUnassign(undefined);
      } catch {
        setConfirmUnassign("error");
      }
    }, [
      onSearch,
      saveInstitutionAssignmentRequest,
      setErrors,
      updateInstitutionAssignment,
    ]);

    const handleAddAssignmentsClicked = useCallback(async () => {
      try {
        const result = await saveInstitutionAssignmentRequest(
          updateInstitutionAssignment
        ).unwrap();

        setErrors(result.validationFailures);

        onSearch();
      } catch {
        setErrors(undefined);
      }
    }, [
      onSearch,
      saveInstitutionAssignmentRequest,
      setErrors,
      updateInstitutionAssignment,
    ]);

    const handleAssignHouseNumberInstitutionClicked = useCallback(async () => {
      const save: SaveSchoolCandidateInstitutionAssignment = {
        ...updateInstitutionAssignment,
        institutionId: undefined,
      };

      try {
        const result = await setInstitutionAssignmentRequest(save).unwrap();

        setErrors(undefined);
        setErrors(result.validationFailures);
        window.scroll(0, 0);

        onSearch();
      } catch {
        setErrors(undefined);
      }
    }, [
      onSearch,
      setErrors,
      setInstitutionAssignmentRequest,
      updateInstitutionAssignment,
    ]);

    const {
      missingAddressWarning,
      handleConfirmDownloadAddresses,
      handleProceedMissingAddress,
      handleCloseMissingAddressWarning,
    } = useDownloadAddresses({
      countyId,
      batchId,
      isSchoolYearEditable,
      tableData,
      setTableData,
      updateInstitutionAssignment,
      setUpdateInstitutionAssignment,
      downloadAddressesRequest,
      setErrors,
      setConfirmUnassign,
      setAssignmentButtons,
    });

    useImperativeHandle(
      ref,
      () => ({
        handleCountyChange,
        handleSchoolCandidateCheckedChange,
        resetAssignmentState,
      }),
      [
        handleCountyChange,
        handleSchoolCandidateCheckedChange,
        resetAssignmentState,
      ]
    );

    return (
      <>
        <FormCard
          cardTitle={t("common.schoolCandidateModel.schoolCandidateAssign")}
        >
          <Row className="m-1 pb-3 pt-3 bg-light border">
            <Col md={2} className="pt-4">
              <Checkbox
                label={t("common.selectAll")}
                name={"selectAll"}
                checked={selectAll}
                onChange={handleSelectAllCheckedChange}
                disabled={!isSchoolYearEditable}
              />
            </Col>
            <Col md={4}>
              <InstitutionSelect
                label={t("common.schoolCandidateModel.institutionList")}
                name={nameof<typeof updateInstitutionAssignment>(
                  "institutionId"
                )}
                value={updateInstitutionAssignment.institutionId}
                onlyByEnrollmentInstitution
                excludeHandicapped
                showUser
                onChange={(event) =>
                  handleAssignmentInstitutionChange(event.target.value)
                }
                countyId={countyId}
              />
            </Col>
            <Col md={6} className="pt-4">
              <Row>
                <Col md={3}>
                  <Button
                    onClick={() => {
                      setIsAddressChangeButtonClicked(true);
                      setConfirmUnassign("confirm");
                    }}
                    outline={true}
                    size="sm"
                    disabled={!isDownloadAddressesEnabled}
                  >
                    Preuzmi adrese
                  </Button>
                </Col>
                <Col md={3}>
                  <Button
                    onClick={() => {
                      setIsAddressChangeButtonClicked(false);
                      setConfirmUnassign("confirm");
                    }}
                    outline={true}
                    size="sm"
                    disabled={!clearAssignmentEnabled}
                  >
                    {t("common.schoolCandidateModel.clearAssignments")}
                  </Button>
                </Col>
                <Col md={3}>
                  <Button
                    onClick={handleAddAssignmentsClicked}
                    size="sm"
                    disabled={!addAssignmentEnabled}
                  >
                    {t("common.schoolCandidateModel.addAssignments")}
                  </Button>
                </Col>
                <Col md={3}>
                  <Button
                    onClick={handleAssignHouseNumberInstitutionClicked}
                    size="sm"
                    disabled={!addHouseNumberInstitutionEnabled}
                  >
                    {t("common.schoolCandidateModel.addHouseNumberInstitution")}
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </FormCard>
        <ConfirmModal
          title={t(
            isAddressChangeButtonClicked
              ? "common.schoolCandidateModel.addressChangeModalTitle"
              : "common.approve"
          )}
          confirmState={confirmUnassign}
          confirmMessage={t(
            isAddressChangeButtonClicked
              ? "common.schoolCandidateModel.addressChangeConfirmMessage"
              : "common.schoolCandidateModel.confirmUnassign"
          )}
          successMesage={t(
            isAddressChangeButtonClicked
              ? "common.schoolCandidateModel.addressChangeSuccessMessage"
              : "common.saveSuccess"
          )}
          errorMessage={t("common.error")}
          onClickClose={() => setConfirmUnassign(undefined)}
          onClickConfirm={
            isAddressChangeButtonClicked
              ? handleConfirmDownloadAddresses
              : handleClearAssignmentsClicked
          }
        />
        <MissingAddressWarningModal
          warning={missingAddressWarning}
          onClose={handleCloseMissingAddressWarning}
          onProceed={handleProceedMissingAddress}
        />
      </>
    );
  }
);
